import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, CreditCard } from "lucide-react";
import { startCheckout } from "@/utils/checkout";

export default function StripeSignin() {
  const { login, user, loading } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      const pending = sessionStorage.getItem("vbs_pending_tier");
      if (pending && searchParams.get("next") === "checkout") {
        sessionStorage.removeItem("vbs_pending_tier");
        startCheckout(pending).catch(() => nav("/dashboard"));
      } else {
        nav("/dashboard", { replace: true });
      }
    }
  }, [loading, user, nav, searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) return toast.error(res.error);
    toast.success("Welcome back");
    const pending = sessionStorage.getItem("vbs_pending_tier");
    if (pending && searchParams.get("next") === "checkout") {
      sessionStorage.removeItem("vbs_pending_tier");
      try { await startCheckout(pending); return; }
      catch { /* fall through */ }
    }
    nav("/dashboard");
  };

  return (
    <div className="vbs-auth-bg" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div className="vbs-card" style={{ width: "100%", maxWidth: 440, padding: 36 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span className="vbs-logo-dot" />
            <span style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: "var(--white)" }}>
              Viral Book Suite
            </span>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: 2, color: "var(--gold-dim)", textTransform: "uppercase" }}>
            Stripe Customers · Sign in
          </div>
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>Email</div>
            <input data-testid="stripe-signin-email" className="vbs-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </label>
          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>Password</div>
            <input data-testid="stripe-signin-password" className="vbs-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          <button data-testid="stripe-signin-submit" type="submit" disabled={busy} className="vbs-btn vbs-btn-gold" style={{ padding: 14, marginTop: 6 }}>
            {busy ? "Signing in…" : "Sign In"}
            <Sparkles size={15} />
          </button>
        </form>

        <div style={{ marginTop: 22, textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
          <Link to="/forgot-password" data-testid="stripe-forgot-link">Forgot password?</Link>
          <span style={{ margin: "0 10px" }}>·</span>
          <Link to="/stripe-signup" data-testid="stripe-signup-link">Create account</Link>
        </div>
        <div style={{ marginTop: 14, fontSize: 11, color: "var(--muted)", textAlign: "center", display: "inline-flex", justifyContent: "center", gap: 6, width: "100%" }}>
          <CreditCard size={12} /> For users who purchased via Stripe
        </div>
      </div>
    </div>
  );
}
